import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Package, CheckCircle, AlertCircle, XCircle, QrCode, History, Camera } from 'lucide-react';
import BarcodeScanner from '@/components/BarcodeScanner';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const ASSET_TYPES = [
  'Fire Extinguisher',
  'Fire Hose Reel',
  'Fire Hydrant',
  'Fire Sprinkler',
  'Fire Alarm Panel',
  'Smoke Detector',
  'Heat Detector',
  'Emergency Lighting',
  'Fire Door',
  'Fire Blanket',
  'Other'
];

const AssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [clients, setClients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [filters, setFilters] = useState({
    client: '',
    asset_type: '',
    compliance_status: ''
  });
  const [formData, setFormData] = useState({
    asset_type: '',
    asset_tag: '',
    serial_number: '',
    manufacturer: '',
    model: '',
    install_date: '',
    client_id: '',
    site_id: '',
    building: '',
    floor: '',
    room: '',
    location_notes: '',
    notes: ''
  });

  useEffect(() => {
    fetchAssets();
    fetchClients();
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchAssets();
  }, [filters]); // eslint-disable-line

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.client && filters.client !== 'all') params.append('client_id', filters.client);
      if (filters.asset_type && filters.asset_type !== 'all') params.append('asset_type', filters.asset_type);
      if (filters.compliance_status && filters.compliance_status !== 'all') params.append('compliance_status', filters.compliance_status);
      
      const response = await axios.get(`${API}/assets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData({ ...formData, client_id: clientId, site_id: '' });
  };

  const handleBarcodeScan = async (scannedCode) => {
    try {
      const token = localStorage.getItem('token');
      // Try to find asset by tag
      const response = await axios.get(`${API}/assets/tag/${scannedCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        toast.success(`Asset found: ${response.data.asset_type}`);
        // Navigate to asset or show details
        handleEdit(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Asset not found with this barcode');
      } else {
        toast.error('Failed to scan barcode');
      }
    }
  };

  const generateBarcode = () => {
    // Generate a unique barcode/asset tag
    const prefix = formData.asset_type.substring(0, 3).toUpperCase() || 'AST';
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    const barcode = `${prefix}-${timestamp}-${randomNum}`;
    setFormData({ ...formData, asset_tag: barcode });
    toast.success('Barcode generated!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingAsset) {
        await axios.put(`${API}/assets/${editingAsset.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Asset updated successfully');
      } else {
        await axios.post(`${API}/assets`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Asset created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    const client = clients.find(c => c.id === asset.client_id);
    setSelectedClient(client);
    setFormData({
      asset_type: asset.asset_type,
      asset_tag: asset.asset_tag,
      serial_number: asset.serial_number || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      install_date: asset.install_date || '',
      client_id: asset.client_id,
      site_id: asset.site_id,
      building: asset.building || '',
      floor: asset.floor || '',
      room: asset.room || '',
      location_notes: asset.location_notes || '',
      notes: asset.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset deleted successfully');
      fetchAssets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete asset';
      toast.error(errorMsg);
      console.error('Error deleting asset:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_type: '',
      asset_tag: '',
      serial_number: '',
      manufacturer: '',
      model: '',
      install_date: '',
      client_id: '',
      site_id: '',
      building: '',
      floor: '',
      room: '',
      location_notes: '',
      notes: ''
    });
    setEditingAsset(null);
    setSelectedClient(null);
  };

  const getComplianceIcon = (status) => {
    switch(status) {
      case 'Compliant':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Non-Compliant':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'Requires Attention':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getComplianceBadge = (status) => {
    const colors = {
      'Compliant': 'bg-green-500',
      'Non-Compliant': 'bg-red-500',
      'Requires Attention': 'bg-yellow-500'
    };
    return <Badge className={colors[status]}>{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Asset Register</h1>
            <p className="text-gray-300 mt-1">Track all fire protection assets</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowScanner(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Camera className="w-4 h-4 mr-2" />
              Scan Barcode
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Asset
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Asset Type</Label>
                    <Select
                      value={formData.asset_type}
                      onValueChange={(value) => setFormData({ ...formData, asset_type: value })}
                      required
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {ASSET_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Asset Tag / Barcode</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.asset_tag}
                        onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })}
                        required
                        placeholder="e.g., EXT-001"
                        className="bg-gray-800 border-gray-700"
                      />
                      <Button type="button" variant="outline" size="icon" onClick={generateBarcode}>
                        <QrCode className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Serial Number</Label>
                    <Input
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Manufacturer</Label>
                    <Input
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </div>

                <div>
                  <Label>Installation Date</Label>
                  <Input
                    type="date"
                    value={formData.install_date}
                    onChange={(e) => setFormData({ ...formData, install_date: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Client</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={handleClientChange}
                      required
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Site</Label>
                    <Select
                      value={formData.site_id}
                      onValueChange={(value) => setFormData({ ...formData, site_id: value })}
                      required
                      disabled={!selectedClient}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {selectedClient?.sites.map((site, idx) => (
                          <SelectItem key={idx} value={site.site_name}>
                            {site.site_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Building</Label>
                    <Input
                      value={formData.building}
                      onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                      placeholder="e.g., Main Building"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Floor</Label>
                    <Input
                      value={formData.floor}
                      onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                      placeholder="e.g., Ground Floor"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Room</Label>
                    <Input
                      value={formData.room}
                      onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                      placeholder="e.g., Room 101"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </div>

                <div>
                  <Label>Location Notes</Label>
                  <Input
                    value={formData.location_notes}
                    onChange={(e) => setFormData({ ...formData, location_notes: e.target.value })}
                    placeholder="e.g., Near main entrance"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600">
                    {editingAsset ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                value={filters.client}
                onValueChange={(value) => setFilters({ ...filters, client: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Filter by client" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.asset_type}
                onValueChange={(value) => setFilters({ ...filters, asset_type: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Types</SelectItem>
                  {ASSET_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.compliance_status}
                onValueChange={(value) => setFilters({ ...filters, compliance_status: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Filter by compliance" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Compliant">Compliant</SelectItem>
                  <SelectItem value="Non-Compliant">Non-Compliant</SelectItem>
                  <SelectItem value="Requires Attention">Requires Attention</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Assets Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((asset) => (
            <Card key={asset.id} className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-500/20 p-2 rounded-lg">
                      <Package className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">{asset.asset_type}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <QrCode className="w-3 h-3 text-gray-400" />
                        <p className="text-xs text-gray-400">{asset.asset_tag}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getComplianceIcon(asset.compliance_status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  {getComplianceBadge(asset.compliance_status)}
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Client:</span>
                  <span className="ml-2 text-white">{asset.client_name}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Location:</span>
                  <span className="ml-2 text-white">
                    {[asset.building, asset.floor, asset.room].filter(Boolean).join(', ') || asset.site_name}
                  </span>
                </div>
                {asset.last_service_date && (
                  <div className="text-sm">
                    <span className="text-gray-400">Last Service:</span>
                    <span className="ml-2 text-white">{asset.last_service_date}</span>
                  </div>
                )}
                {asset.next_service_due && (
                  <div className="text-sm">
                    <span className="text-gray-400">Next Service:</span>
                    <span className="ml-2 text-white">{asset.next_service_due}</span>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(asset)}
                    className="text-blue-400"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-400"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(asset.id)}
                    className="text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {assets.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No assets found. Add your first asset to get started.</p>
          </div>
        )}
      </div>

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
      />
    </AdminLayout>
  );
};

export default AssetsPage;
